import { createSelector } from 'reselect'
import { milisecToHoursMinutes } from '../../../helper'
import { selectCompanyData } from '../system'
import { getTimeZoneByCountryCode } from '../../../helper/getTimeZone'
import { utcToZonedTime } from 'date-fns-tz'

export const selectTimerCounter = store => store.timerCounter.counter
export const selectTableTimerLimit = store => store.restaurantSettings.tableTimerConfigs?.timerLimit

export const selectRemainingMinutes = (duration, modifiedDate) => createSelector(
	selectTimerCounter,
	(counter) => {
		const modifiedDateObject = new Date(modifiedDate)
		const currentTime = new Date()
		const returnTime = new Date(modifiedDateObject.getTime() + (duration * 1000))
		if (currentTime.getTime() < returnTime.getTime()) {
			const timeDiff = returnTime.getTime() - currentTime.getTime()
			return Math.floor(timeDiff / (1000 * 60))
		}
		const add5Min = returnTime.getTime() + (5 * 60 * 1000)

		if (currentTime.getTime() <= add5Min) {
			const timeDiff = add5Min - currentTime.getTime()
			return timeDiff > 0 ? 0 : null
		}
		return null
	}
)

export const selectRemainingTime = (estimatedProcessTimestamp, hoursSign = 'h', minutesSign = 'm') => createSelector(
	selectTimerCounter,
	(counter) => {
		if (!estimatedProcessTimestamp) return null
		const estimatedProcessTimestampObject = new Date(estimatedProcessTimestamp)
		const currentTime = new Date()
		const leftTime = new Date(estimatedProcessTimestampObject.getTime() - currentTime.getTime())

		if (currentTime.getTime() > estimatedProcessTimestampObject.getTime()) {
			const delayTime = currentTime.getTime() - estimatedProcessTimestampObject.getTime()
			return milisecToHoursMinutes(delayTime, hoursSign, minutesSign)
		} else {
			return milisecToHoursMinutes(leftTime, hoursSign, minutesSign)
		}
	}
)

export const selectIsDriverLate = (estimatedProcessTimestamp) => createSelector(
	selectTimerCounter,
	(counter) => {
		if (!estimatedProcessTimestamp) return null
		const estimatedProcessTimestampObject = new Date(estimatedProcessTimestamp)
		const currentTime = new Date()

		return currentTime.getTime() > estimatedProcessTimestampObject.getTime()
	}
)

export const selectTimeSinceLastOrder = (lastOrderDate) => createSelector(
	selectTimerCounter,
	selectCompanyData('countryCode'),
	(counter, companyCountryCode) => {
		if (!lastOrderDate) return null

		const companyTimeZone = getTimeZoneByCountryCode(companyCountryCode) || 'Europe/Berlin'
		const dateTimeZone = utcToZonedTime(lastOrderDate, companyTimeZone)
		const lastOrderDateObject = new Date(dateTimeZone)
		const currentTime = utcToZonedTime(new Date(), companyTimeZone)

		const elapsedTime = currentTime.getTime() - lastOrderDateObject.getTime()

		const totalMinutes = Math.floor(elapsedTime / (1000 * 60))
		const hours = Math.floor(totalMinutes / 60)
		const minutes = totalMinutes % 60

		const formattedHours = hours.toString().padStart(2, '0')
		const formattedMinutes = minutes.toString().padStart(2, '0')

		return `${formattedHours}:${formattedMinutes}`
	},
)

export const selectIsTimerLimitExceeded = (lastOrderDate) => createSelector(
	selectTimerCounter,
	selectTableTimerLimit,
	selectCompanyData('countryCode'),
	(counter, timerLimit, companyCountryCode) => {
		if (!lastOrderDate || !timerLimit) return false

		const companyTimeZone = getTimeZoneByCountryCode(companyCountryCode) || 'Europe/Berlin'
		const dateTimeZone = utcToZonedTime(lastOrderDate, companyTimeZone)

		const [hours, minutes, seconds] = timerLimit.split(':').map(Number)
		const timerLimitInMinutes = (hours * 60) + minutes + (seconds / 60)

		const lastOrderDateObject = new Date(dateTimeZone)
		const currentTime = utcToZonedTime(new Date(), companyTimeZone)

		const elapsedMinutes = Math.floor(
			(currentTime.getTime() - lastOrderDateObject.getTime()) / (1000 * 60),
		)

		return elapsedMinutes >= timerLimitInMinutes
	},
)
